// client/src/pages/LgmProductNavigator.tsx
import { useState, useEffect, useCallback } from 'react';
import ProductsReview from './ProductsReview';
import ProductMasterReview from './ProductMasterReview';
import ManufacturerReview from './ManufacturerReview';
import Grades from './Grades';
import TherapeuticAreas from './TherapeuticAreas';
import ManufacturerMap from './ManufacturerMap';
import useAuth from 'auth/auth';
import DevTools from 'components/DevTools';
import QuotesReview from './QuotesReview';
import { useDeleteAllProducts } from 'services/api/domains/products/hooks';
import AuditLogs from './AuditLogs';
import DashboardMetrics from './DashboardMetrics';

const LgmProductNavigator: React.FC = () => {
  const [view, setView] = useState<
    | 'products'
    | 'master'
    | 'manufacturer_master'
    | 'manufacturer_map'
    | 'grades'
    | 'therapeutic_areas'
    | 'quotes'
    | 'audit_logs'
    | 'dashboard_metrics'
  >('products');

  const { instance, account, isAuthenticated, isAdmin, roles } = useAuth();

  const [showDevTools, setShowDevTools] = useState(false);
  const deleteAllProductsMutation = useDeleteAllProducts();

  useEffect(() => {
    if (account) {
      setView('products');
    }
  }, [instance, account]);

  useEffect(() => {
    console.log('🔎 Current view:', view, 'Roles:', roles);
  }, [view, roles]);

  const handleDeleteAll = useCallback(async () => {
    if (!window.confirm('⚠️ Delete ALL products? This cannot be undone.')) {
      return;
    }
    try {
      await deleteAllProductsMutation.mutate();
    } catch (err: unknown) {
      alert(`❌ Failed to delete all products: ${JSON.stringify(err)}`);
    }
  }, [deleteAllProductsMutation]);

  useEffect(() => {
    if (deleteAllProductsMutation.status === 'success') {
      alert('✅ All products deleted successfully');
    }
  }, [deleteAllProductsMutation.status]);

  return (
    <div style={styles.mainContainerStyle}>
      {/* Top Navigation Bar */}
      <div style={styles.headerContainerStyle}>
        <div style={styles.headerContentStyle}>
          <img
            src="/LGMNavigator.png"
            alt="LGM Pharma"
            style={styles.logoStyle}
          />

          {/* Navigation Buttons */}
          <div style={styles.navContainerStyle}>
            <button
              onClick={() => setView('products')}
              style={
                view === 'products'
                  ? styles.navButtonActiveStyle
                  : styles.navButtonStyle
              }>
              Products
            </button>
            <button
              onClick={() => setView('manufacturer_master')}
              style={
                view === 'manufacturer_master'
                  ? styles.navButtonActiveStyle
                  : styles.navButtonStyle
              }>
              Manufacturers
            </button>
            {isAdmin && (
              <>
                <button
                  onClick={() => setView('grades')}
                  style={
                    view === 'grades'
                      ? styles.navButtonActiveStyle
                      : styles.navButtonStyle
                  }>
                  Product Types
                </button>
                <button
                  onClick={() => setView('therapeutic_areas')}
                  style={
                    view === 'therapeutic_areas'
                      ? styles.navButtonActiveStyle
                      : styles.navButtonStyle
                  }>
                  Therapeutic Areas
                </button>
              </>
            )}
            <button
              onClick={() => setView('manufacturer_map')}
              style={
                view === 'manufacturer_map'
                  ? styles.navButtonActiveStyle
                  : styles.navButtonStyle
              }>
              Facilities Map
            </button>
            <button
              disabled
              style={styles.navButtonDisabledStyle}
              title="Disabled for now">
              RFQs
            </button>
            <button
              onClick={() => setView('dashboard_metrics')}
              style={
                view === 'dashboard_metrics'
                  ? styles.navButtonActiveStyle
                  : styles.navButtonStyle
              }>
              Metrics
            </button>
            {isAdmin && (
              <button
                onClick={() => setView('audit_logs')}
                style={
                  view === 'audit_logs'
                    ? styles.navButtonActiveStyle
                    : styles.navButtonStyle
                }>
                Audit Logs
              </button>
            )}
          </div>

          <div style={styles.buttonGroupStyle}>
            <button
              onClick={() => setShowDevTools(prev => !prev)}
              style={styles.headerButtonStyle}>
              {showDevTools ? 'Hide Dev Tools' : 'Show Dev Tools'}
            </button>
            <button
              onClick={() => instance.logoutRedirect()}
              style={styles.headerButtonStyle}>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Developer Tools Panel */}
      {showDevTools && (
        <DevTools
          deleteAllStatus={deleteAllProductsMutation}
          onDeleteAll={handleDeleteAll}
        />
      )}

      {/* Main Content */}
      <div style={styles.mainContentStyle}>
        {!isAuthenticated ? (
          <p style={styles.waitingTextStyle}>🔐 Waiting for login...</p>
        ) : (
          <>
            {/* Products */}
            {view === 'products' && (
              <ProductsReview deleteAllStatus={deleteAllProductsMutation} />
            )}
            {/* Manufacturers */}
            {view === 'manufacturer_master' && <ManufacturerReview />}
            {/* NOTE: Master Products is not used */}
            {view === 'master' && <ProductMasterReview />}
            {/* Grades */}
            {view === 'grades' && isAdmin && <Grades />}
            {/* Therapeutic Areas */}
            {view === 'therapeutic_areas' && isAdmin && <TherapeuticAreas />}
            {/* Facilities Map */}
            {view === 'manufacturer_map' && <ManufacturerMap />}
            {/* RFQs */}
            {view === 'quotes' && <QuotesReview />}
            {/* Dashboard Metrics */}
            {view === 'dashboard_metrics' && <DashboardMetrics />}
            {/* Audit Logs */}
            {view === 'audit_logs' && <AuditLogs />}
          </>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  mainContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    fontFamily: 'inherit', // font-lgm
  } as React.CSSProperties,
  headerContainerStyle: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: 'white',
    borderBottom: '1px solid #e5e7eb', // border-b
    paddingBottom: '1rem', // pb-4
    paddingLeft: '3rem', // px-12
    paddingRight: '3rem',
    paddingTop: '1rem', // py-4
  } as React.CSSProperties,
  headerContentStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '2rem',
  } as React.CSSProperties,
  logoStyle: {
    height: '75px',
    objectFit: 'contain',
  } as React.CSSProperties,
  buttonGroupStyle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem', // gap-3
  } as React.CSSProperties,
  headerButtonStyle: {
    backgroundColor: '#f3f4f6', // bg-gray-100
    color: '#1f2937', // text-gray-800
    padding: '0.25rem 0.75rem', // px-3 py-1
    borderRadius: '0.25rem', // rounded
    fontSize: '0.875rem', // text-sm
    border: 'none',
    cursor: 'pointer',
  } as React.CSSProperties,
  navContainerStyle: {
    display: 'flex',
    gap: '1.5rem', // space-x-6
    color: '#6b7280', // text-gray-500
    fontSize: '0.875rem', // text-sm
    flex: 1,
    justifyContent: 'flex-start',
  } as React.CSSProperties,
  navButtonStyle: {
    color: '#6b7280', // text-gray-500
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.875rem',
  } as React.CSSProperties,
  navButtonActiveStyle: {
    color: '#ea580c', // text-orange-600
    fontWeight: 'bold',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1.25rem',
  } as React.CSSProperties,
  navButtonDisabledStyle: {
    color: '#9ca3af', // text-gray-400
    cursor: 'not-allowed',
    background: 'none',
    border: 'none',
    fontSize: '0.875rem',
  } as React.CSSProperties,
  mainContentStyle: {
    flex: 1,
    overflow: 'auto',
    paddingLeft: '3rem', // px-12
    paddingRight: '3rem',
    paddingTop: '1.5rem', // py-6
    paddingBottom: '1.5rem',
  } as React.CSSProperties,
  waitingTextStyle: {
    color: '#6b7280', // text-gray-500
  } as React.CSSProperties,
};

export default LgmProductNavigator;
